import React, { useRef, useState, useLayoutEffect, ReactNode, CSSProperties } from "react"

export function ExpandableContent({
    isOpen,
    children,
    transitionDurationMs = 250,
    direction = "height",
}: {
    isOpen: boolean
    children: ReactNode
    transitionDurationMs?: number
    direction?: "height" | "width"
}) {
    const ref = useRef<HTMLDivElement>(null)
    const isMounted = useRef(false)
    const [size, setSize] = useState<number | "auto">(isOpen ? "auto" : 0)
    const [overflow, setOverflow] = useState<"hidden" | "visible">(isOpen ? "visible" : "hidden")
    const sizeRef = useRef(size)
    sizeRef.current = size

    useLayoutEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true
            return
        }
        if (!ref.current) return
        const el = ref.current
        if (isOpen) {
            setOverflow("hidden")
            const fullSize = direction === "width" ? el.scrollWidth : el.scrollHeight
            setSize(fullSize)
            const t = setTimeout(() => {
                setOverflow("visible")
                setSize("auto")
            }, transitionDurationMs)
            return () => {
                clearTimeout(t)
            }
        } else {
            setOverflow("hidden")
            if (sizeRef.current === 0) return
            const rect = el.getBoundingClientRect()
            const current = direction === "width" ? rect.width : rect.height
            setSize(current)
            let innerRaf: number | undefined
            const outerRaf = requestAnimationFrame(() => {
                innerRaf = requestAnimationFrame(() => {
                    setSize(0)
                })
            })
            return () => {
                cancelAnimationFrame(outerRaf)
                if (innerRaf !== undefined) cancelAnimationFrame(innerRaf)
            }
        }
    }, [isOpen, direction, transitionDurationMs])

    // Block-level width:auto fills the container instead of shrinking to content,
    // so the settled state needs fit-content to mirror height:auto's content-based sizing.
    const sizeValue =
        size === "auto" ? (direction === "width" ? "fit-content" : "auto") : `${size}px`

    const containerStyle: CSSProperties = {
        overflow,
        willChange: direction,
        transitionProperty: direction,
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        transitionDuration: `${transitionDurationMs}ms`,
    }
    if (direction === "width") {
        containerStyle.width = sizeValue
    } else {
        containerStyle.height = sizeValue
    }

    return (
        <div style={containerStyle}>
            {/* max-content (not inline-block) so the shrink-to-fit formula can't clamp this to the outer div's current animating width */}
            <div ref={ref} style={direction === "width" ? { width: "max-content" } : undefined}>
                {children}
            </div>
        </div>
    )
}
